/**
 * StatusPage — route wrapper at /status.
 *
 * Project development progress dashboard for yan-vada internal use.
 * Reads typed data from data/status-data.ts (derived from 06-data/*.json).
 *
 * Renders:
 *   - Phase banner + session info
 *   - Episode pipeline (visual cards)
 *   - KEI indicators (Convergence primary pre-prod metric)
 *   - Active blockers list
 *   - Task summary (P0/P1/P2 counts)
 *   - Budget bar chart
 *   - Gate status
 *   - Team roster
 */

import { projectStatus } from '../data/status-data'

/* ---------- Sub-components ---------- */

function PhaseBanner() {
  const { project, episodes } = projectStatus
  return (
    <div className="status-phase-banner">
      <div className="status-phase-label">
        <span className="legal-dot" /> {project.phase}
      </div>
      <h1 className="status-heading">
        {project.name} &middot; {project.sinhalaName}
      </h1>
      <p className="status-sub">
        Session {project.session} &middot; Last updated {project.lastUpdated}
        &middot; Recording window: {project.recordingWindow}
      </p>
      <div className="status-episode-summary">
        <span className="stat-num">{episodes.planned}</span>
        <span className="stat-label">episodes planned</span>
        <span className="stat-sep" />
        <span className="stat-num">{episodes.recorded}</span>
        <span className="stat-label">recorded</span>
        <span className="stat-sep" />
        <span className="stat-num">{episodes.published}</span>
        <span className="stat-label">published</span>
      </div>
    </div>
  )
}

function EpisodePipeline() {
  const { episodes } = projectStatus
  return (
    <section className="status-section">
      <h2 className="status-section-title">Episode Pipeline</h2>
      <div className="episode-pipeline">
        {episodes.list.map(ep => (
          <div key={ep.id} className={`episode-card episode-${ep.status}`}>
            <div className="episode-card-header">
              <span className="episode-id">{ep.id}</span>
              <span className={`episode-status-badge episode-status-${ep.status}`}>{ep.status}</span>
            </div>
            <h3 className="episode-card-title">{ep.title || 'Untitled'}</h3>
            {ep.guest && <p className="episode-guest">{ep.guest}</p>}
            <div className="episode-research">
              <span className="episode-research-label">Research:</span>
              <span>{ep.researchStatus}</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

function KEIIndicators() {
  const { kei } = projectStatus
  return (
    <section className="status-section">
      <h2 className="status-section-title">KEI Indicators</h2>
      <p className="status-note">{kei.note}</p>
      <p className="status-formula">{kei.formula}</p>
      <div className="kei-grid">
        {kei.dimensions.map(dim => (
          <div key={dim.code} className={`kei-dimension kei-${dim.code.toLowerCase()}`}>
            <div className="kei-dim-header">
              <span className="kei-dim-name">{dim.name}</span>
              <span className="kei-dim-code">{dim.code}</span>
              <span className="kei-dim-weight">w{dim.weight}</span>
            </div>
            <p className="kei-dim-desc">{dim.description}</p>
            <div className="kei-metrics">
              {dim.metrics.map((m, i) => (
                <div key={i} className="kei-metric">
                  <span className="kei-metric-name">{m.metric}</span>
                  <span className="kei-metric-values">
                    <span className="kei-current">{m.current ?? '—'}</span>
                    <span className="kei-target">&rarr; {m.target}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

function Blockers() {
  const { blockers } = projectStatus
  return (
    <section className="status-section">
      <h2 className="status-section-title">
        Active Blockers ({blockers.active.length})
      </h2>
      <div className="blocker-list">
        {blockers.active.map((b, i) => (
          <div key={i} className={`blocker-item blocker-${b.priority.toLowerCase()}`}>
            <span className={`blocker-priority blocker-prio-${b.priority.toLowerCase()}`}>{b.priority}</span>
            <span className="blocker-text">{b.text}</span>
            <span className="blocker-owner">{b.owner}</span>
          </div>
        ))}
      </div>
      {blockers.resolved.length > 0 && (
        <details className="resolved-blockers">
          <summary>Resolved this session ({blockers.resolved.length})</summary>
          <ul>
            {blockers.resolved.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </details>
      )}
    </section>
  )
}

function TaskSummary() {
  const { tasks } = projectStatus
  return (
    <section className="status-section">
      <h2 className="status-section-title">Tasks ({tasks.total} total)</h2>
      <div className="task-summary-grid">
        <div className="task-stat task-p0">
          <span className="task-stat-num">{tasks.p0}</span>
          <span className="task-stat-label">P0 Critical</span>
        </div>
        <div className="task-stat task-p1">
          <span className="task-stat-num">{tasks.p1}</span>
          <span className="task-stat-label">P1 High</span>
        </div>
        <div className="task-stat task-p2">
          <span className="task-stat-num">{tasks.p2}</span>
          <span className="task-stat-label">P2 Medium</span>
        </div>
        <div className="task-stat task-done">
          <span className="task-stat-num">{tasks.done}</span>
          <span className="task-stat-label">Done</span>
        </div>
      </div>
      <div className="task-breakdown">
        <div className="task-bar-row">
          <span className="task-bar-label">pending</span>
          <div className="task-bar">
            <div className="task-bar-fill task-bar-pending" style={{ width: `${(tasks.pending / tasks.total) * 100}%` }} />
          </div>
          <span className="task-bar-val">{tasks.pending}</span>
        </div>
        <div className="task-bar-row">
          <span className="task-bar-label">waiting</span>
          <div className="task-bar">
            <div className="task-bar-fill task-bar-waiting" style={{ width: `${(tasks.waiting / tasks.total) * 100}%` }} />
          </div>
          <span className="task-bar-val">{tasks.waiting}</span>
        </div>
        <div className="task-bar-row">
          <span className="task-bar-label">blocked</span>
          <div className="task-bar">
            <div className="task-bar-fill task-bar-blocked" style={{ width: `${(tasks.blocked / tasks.total) * 100}%` }} />
          </div>
          <span className="task-bar-val">{tasks.blocked}</span>
        </div>
      </div>
    </section>
  )
}

function BudgetOverview() {
  const { budget } = projectStatus
  const pctSpent = ((budget.spentEur / budget.ceilingEur) * 100).toFixed(1)
  const pctCommitted = (((budget.spentEur + budget.committedEur) / budget.ceilingEur) * 100).toFixed(1)
  return (
    <section className="status-section">
      <h2 className="status-section-title">Budget</h2>
      <p className="status-note">{budget.scopeNote}</p>
      <p className="status-note">{budget.futureLedgerNote}</p>
      <div className="budget-overview">
        <div className="budget-big-numbers">
          <div className="budget-stat">
            <span className="budget-stat-num">&euro;{budget.spentEur.toLocaleString()}</span>
            <span className="budget-stat-label">spent ({pctSpent}%)</span>
          </div>
          <div className="budget-stat">
            <span className="budget-stat-num">&euro;{budget.committedEur.toLocaleString()}</span>
            <span className="budget-stat-label">committed</span>
          </div>
          <div className="budget-stat">
            <span className="budget-stat-num budget-remaining">&euro;{budget.remainingEur.toLocaleString()}</span>
            <span className="budget-stat-label">remaining</span>
          </div>
          <div className="budget-stat">
            <span className="budget-stat-num">&euro;{budget.ceilingEur.toLocaleString()}</span>
            <span className="budget-stat-label">ceiling</span>
          </div>
        </div>
        <div className="budget-bar-wrapper">
          <div className="budget-bar">
            <div className="budget-bar-spent" style={{ width: pctSpent + '%' }} />
            <div className="budget-bar-committed" style={{ width: pctCommitted + '%' }} />
          </div>
          <div className="budget-bar-legend">
            <span><span className="legend-dot legend-spent" /> Spent</span>
            <span><span className="legend-dot legend-committed" /> Committed</span>
            <span><span className="legend-dot legend-remaining" /> Remaining</span>
          </div>
        </div>
      </div>
      <div className="budget-categories">
        {budget.categories.map((cat, i) => (
          <div key={i} className="budget-cat">
            <div className="budget-cat-header">
              <span className="budget-cat-name">{cat.name}</span>
              <span className="budget-cat-amount">&euro;{cat.spent.toLocaleString()}</span>
            </div>
            {cat.committed > 0 && (
              <span className="budget-cat-committed">+ &euro;{cat.committed.toLocaleString()} committed</span>
            )}
            <p className="budget-cat-desc">{cat.description}</p>
          </div>
        ))}
      </div>
    </section>
  )
}

function GateStatus() {
  const { gates } = projectStatus
  const cleared = gates.filter(g => g.status === 'cleared').length
  return (
    <section className="status-section">
      <h2 className="status-section-title">
        Gates ({cleared}/{gates.length} cleared)
      </h2>
      <div className="gate-list">
        {gates.map(g => (
          <div key={g.id} className={`gate-item gate-${g.status}`}>
            <span className="gate-id">{g.id}</span>
            <span className="gate-label">{g.label}</span>
            <span className={`gate-status-dot gate-${g.status}`} />
          </div>
        ))}
      </div>
    </section>
  )
}

function TeamRoster() {
  const { team } = projectStatus
  return (
    <section className="status-section">
      <h2 className="status-section-title">Team</h2>
      <div className="team-roster">
        <div className="team-group">
          <span className="team-group-label">Active ({team.active.length})</span>
          {team.active.map((m, i) => (
            <span key={i} className="team-member team-active">{m}</span>
          ))}
        </div>
        <div className="team-group">
          <span className="team-group-label">In talks ({team.inTalks.length})</span>
          {team.inTalks.map((m, i) => (
            <span key={i} className="team-member team-intalks">{m}</span>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ---------- Main page ---------- */

export default function StatusPage() {
  return (
    <div className="status-page">
      <PhaseBanner />
      <EpisodePipeline />
      <KEIIndicators />
      <div className="status-two-col">
        <div>
          <Blockers />
          <GateStatus />
        </div>
        <div>
          <TaskSummary />
          <TeamRoster />
        </div>
      </div>
      <BudgetOverview />
      <footer className="status-footer">
        Data sourced from Yan-Vada workspace 06-data/*.json &middot; Session 16 &middot;
        Auto-sync not yet implemented — manual snapshot
      </footer>
    </div>
  )
}
